import { Modal, Button, Row, Col, Badge, Card } from 'react-bootstrap';
import { FaTimes, FaFileContract, FaMoneyBillWave, FaCalendar, FaUser } from 'react-icons/fa';

// Composants

// Utilitaires
import { formatAmountInThousands, formatDate } from '../../service/utils/formatters';
import { formatCurrencys } from '../../service/utils/facturationUtils';
import { getStatusClass } from '../../service/utils/orderUtils';
import TransactionDocuments from './Documents/TransactionDocuments';
import './style/TransactionDetails.css';

/**
 * Modal détaillé d'affichage des informations d'une transaction
 * Présente toutes les informations de la transaction ainsi que les documents associés
 * 
 * @param {Object} props
 * @param {boolean} props.show - Contrôle l'affichage du modal
 * @param {Function} props.onHide - Callback de fermeture du modal
 * @param {Object} props.transaction - Données de la transaction à afficher
 * @param {number} props.domainId - ID du domaine pour la sécurité des documents
 */
const TransactionDetailsModal = ({ show, onHide, transaction, domainId }) => {
  
  /**
   * Calcule le prix total de la transaction
   * @returns {number} Prix total formaté
   */
  const calculateTotalPrice = () => {
    if (!transaction?.price || !transaction?.quantity) return 0;
    return transaction.price * transaction.quantity;
  };

  /**
   * Formate le type d'ordre avec un badge coloré
   * @param {string} side - Type d'ordre (Buy/Sell/Souscription)
   * @returns {JSX.Element} Badge React formaté
   */
  const renderOrderTypeBadge = (side) => {
    const badgeConfig = {
      'Buy': {
        bg: 'success',
        text: 'text-success',
        border: 'border-success',
        label: 'Achat'
      },
      'Sell': {
        bg: 'danger', 
        text: 'text-danger',
        border: 'border-danger',
        label: 'Vente'
      },
      'Souscription': {
        bg: 'info',
        text: 'text-info', 
        border: 'border-info',
        label: 'Souscription'
      },
      'default': {
        bg: 'secondary',
        text: 'text-secondary',
        border: 'border-secondary',
        label: side || 'Inconnu'
      }
    };

    const config = badgeConfig[side] || badgeConfig.default;

    return (
      <Badge 
        className={`
          bg-${config.bg}-opacity-10 
          ${config.text} 
          border 
          border-${config.border} 
          border-opacity-25 
          px-3 
          py-2
        `}
      >
        {config.label}
      </Badge>
    );
  };

  // Si aucune transaction n'est fournie, ne rien afficher
  if (!transaction) {
    return null;
  }

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      size="xl"
      className="transaction-details-modal"
    >
      {/* En-tête du modal */}
      <Modal.Header closeButton className="border-bottom-0 bg-light">
        <Modal.Title className="d-flex align-items-center w-100">
          <FaFileContract className="text-primary me-3" size={24} />
          <div className="flex-grow-1">
            <h4 className="mb-1 text-dark">Détails de la Transaction</h4>
            <p className="text-muted mb-0">Transaction #{transaction.id}</p>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-0 mt-3">
        <Row className="g-4">
          {/* Colonne des informations principales */}
          <Col lg={6}>
            {/* Carte des informations de base */}
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-transparent border-bottom-0">
                <h5 className="mb-0 d-flex align-items-center">
                  <FaFileContract className="text-primary me-2" />
                  Informations principales
                </h5>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  {/* ID de l'ordre */}
                  <Col sm={6}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Numéro d'ordre:</span>
                      <Badge bg="outline-primary" text="dark">
                        #{transaction.orderId}
                      </Badge>
                    </div>
                  </Col>

                  {/* Type d'ordre */}
                  <Col sm={6}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Type d'ordre:</span>
                      {renderOrderTypeBadge(transaction.side)}
                    </div>
                  </Col>

                  {/* Créateur */}
                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted d-flex align-items-center">
                        <FaUser className="me-2 text-primary" size={14} />
                        Créateur:
                      </span>
                      <span className="fw-medium text-end">
                        {transaction.creatorName || 'Non spécifié'}
                      </span>
                    </div>
                  </Col>

                  {/* Émetteur */}
                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Émetteur:</span>
                      <span className="fw-medium text-end">
                        {transaction.fromAccountEmail || 'Non spécifié'}
                      </span>
                    </div>
                  </Col>

                  {/* Destinataire */}
                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Destinataire:</span>
                      <span className="fw-medium text-end">
                        {transaction.toAccountEmail || 'Non spécifié'}
                      </span>
                    </div>
                  </Col>

                  {/* Statut */}
                  <Col sm={6}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Statut : </span>
                      <span className={`rounded-pill px-2 py-2 ${getStatusClass(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Colonne des informations financières */}
          <Col lg={6}>
            {/* Carte des détails financiers */}
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-transparent border-bottom-0">
                <h5 className="mb-0 d-flex align-items-center">
                  <FaMoneyBillWave className="text-success me-2" />
                  Détails financiers
                </h5>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  {/* Prix unitaire */}
                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Prix unitaire:</span>
                      <span className="fw-semibold">
                        {formatCurrencys(transaction.price)} Fcfa
                      </span>
                    </div>
                  </Col>

                  {/* Quantité */}
                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Quantité:</span>
                      <span className="fw-semibold">
                        {transaction.quantity?.toLocaleString()}
                      </span>
                    </div>
                  </Col>

                  {/* Prix total */}
                  <Col sm={12}>
                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                      <span className="text-muted fw-bold">Prix total:</span>
                      <span className="counter-amount-badge fs-5">
                        {formatAmountInThousands(calculateTotalPrice())}
                      </span>
                    </div>
                  </Col>

                  <Col sm={6}>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Date de création:</span>
                      <span className="fw-medium">
                        {formatDate(transaction.creationDate)}
                      </span>
                    </div>
                  </Col>

                  {transaction.lastUpdateDate && (
                    <Col sm={6}>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Dernière modification:</span>
                        <span className="fw-medium">
                          {formatDate(transaction.lastUpdateDate)}
                        </span>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Section des documents - Affichée seulement si domainId est disponible */}
        {domainId && (
          <TransactionDocuments
            transactionId={transaction.id}
            domainId={domainId}
            className="mt-4"
          />
        )}
      </Modal.Body>
      

      {/* Pied de page du modal */}
      <Modal.Footer className="border-top-0 bg-light">
        <Button variant="outline-secondary" onClick={onHide}>
          <FaTimes className="me-2" />
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransactionDetailsModal;